import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

// Redux Core Actions & Operation Thunks
import { setCurrentPage, setSelectedHomeworkMaster } from "../../redux/admin/homeworkMaster.slice";
import { fetchHomeworkMasters, addHomeworkMaster, updateHomeworkMaster } from "../../redux/admin/homeworkMaster.thunk";

// Cross-Slice Dropdown & Resolution Roster Hydration Thunks
import { fetchSessionMasters } from "../../redux/admin/sessionMaster.thunk";
import { fetchStudents } from "../../redux/admin/student.thunk";
import { fetchCourseCategories } from "../../redux/admin/courseCategory.thunk";
import { fetchBranches } from "../../redux/admin/branch.thunk";

import toastr from "toastr";
import "toastr/build/toastr.min.css";

/* ─── Spinner Component ─── */
const Spinner = ({ size = 16, color = "#ffffff", thickness = 2 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    style={{ animation: "hm-spin 0.72s linear infinite", flexShrink: 0 }}
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" stroke={color} strokeOpacity="0.22" strokeWidth={thickness} />
    <path d="M12 2a10 10 0 0 1 10 10" stroke={color} strokeWidth={thickness} strokeLinecap="round" />
  </svg>
);

/* ─── Skeleton Shimmer Loader ─── */
const Skeleton = ({ w = "100%", h = 14, r = 6, style: extra = {} }) => (
  <div
    style={{
      width: w, height: h, borderRadius: r,
      background: "linear-gradient(90deg,#f0f0f5 25%,#e8e8ed 50%,#f0f0f5 75%)",
      backgroundSize: "600px 100%",
      animation: "hm-shimmer 1.4s infinite linear",
      ...extra,
    }}
  />
);

const HomeworkList = () => {
  const dispatch = useDispatch();
  const limit = 10;

  /* =====================================================
     REDUX SELECTORS
  ====================================================== */
  const homeworkState = useSelector((state) => state.admin?.homeworkMaster || {});
  const sessionMasterState = useSelector((state) => state.admin?.sessionMaster || {});
  const studentsState = useSelector((state) => state.admin?.students || {});
  const courseCategoryState = useSelector((state) => state.admin?.courseCategory || {});
  const branchState = useSelector((state) => state.admin?.branches || {});

  const { homeworkMasters = [], totalPages = 1, currentPage = 1, total = 0, loading = false, selectedHomeworkMaster = null } = homeworkState;
  const masterSessionsList = sessionMasterState.sessionMasters || [];
  const globalStudents = studentsState.studentList || studentsState.students || [];
  const globalCategories = courseCategoryState.courseCategories || courseCategoryState.categories || [];
  const globalBranches = branchState.branches || branchState.branchList || [];

  /* =====================================================
     SEARCH & MODAL STATES
  ====================================================== */
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState("details");
  const [saving, setSaving] = useState(false);

  /* =====================================================
     INITIAL FORM LAYOUT STATE
  ====================================================== */
  const initialFormState = {
    subject: "",
    topic: "",
    title: "",
    description: "",
    external_link: "",
    assignment_type: "session", 
    session_master_ids: [],     
    student_ids: [],            
    due_date: "",
  };

  const [formData, setFormData] = useState(initialFormState);
  const [studentSearch, setStudentSearch] = useState("");
  const [sessionSearch, setSessionSearch] = useState("");

  /* =====================================================
     SEARCH DEBOUNCE EFFECT
  ====================================================== */
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 600);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  /* =====================================================
     FETCH OPERATION LIFECYCLE MANAGEMENT
  ====================================================== */
  useEffect(() => {
    dispatch(fetchHomeworkMasters({ page: currentPage, limit, search: debouncedSearch }));
  }, [dispatch, currentPage, debouncedSearch]);

  useEffect(() => {
    dispatch(fetchSessionMasters({ page: 1, limit: 1000 }));
    dispatch(fetchStudents({ page: 1, limit: 1000, status: "active" }));
    dispatch(fetchCourseCategories());
    dispatch(fetchBranches({ page: 1, limit: 1000 }));
  }, [dispatch]);

  /* =====================================================
     HANDLERS & STRUCTURAL TOGGLES
  ====================================================== */
  const handlePageChange = (page) => { if (page >= 1 && page <= totalPages) dispatch(setCurrentPage(page)); };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const toggleSessionSelection = (id) => {
    setFormData((prev) => {
      const isSelected = prev.session_master_ids.includes(Number(id));
      const updatedIds = isSelected 
        ? prev.session_master_ids.filter((sid) => sid !== Number(id))
        : [...prev.session_master_ids, Number(id)];
      return { ...prev, session_master_ids: updatedIds };
    });
  };

  const toggleStudentSelection = (id) => {
    setFormData((prev) => {
      const isSelected = prev.student_ids.includes(Number(id));
      const updatedIds = isSelected 
        ? prev.student_ids.filter((sid) => sid !== Number(id))
        : [...prev.student_ids, Number(id)];
      return { ...prev, student_ids: updatedIds };
    });
  };

  const openAddModal = () => {
    setIsEditMode(false);
    setFormData(initialFormState);
    setStudentSearch("");
    setSessionSearch("");
    setActiveTab("details");
    setShowModal(true);
  };

  const cloneHomework = (homework) => {
    setIsEditMode(false);
    
    let finalDueDate = homework.due_date || "";
    if (!finalDueDate && homework.assignments && homework.assignments[0]) {
      finalDueDate = homework.assignments[0].due_date || "";
    }

    setFormData({
      subject: homework.subject || "",
      topic: homework.topic || "",
      title: homework.title || "",
      description: homework.description || "",
      external_link: homework.external_link || "",
      assignment_type: homework.assignment_type || "session",
      session_master_ids: [],
      student_ids: [],
      due_date: finalDueDate ? finalDueDate.replace(" ", "T").substring(0, 16) : "",
    });

    setStudentSearch("");
    setSessionSearch("");
    setActiveTab("target");
    setShowModal(true);
    toastr.info("Task cloned! Select the target branch batch and due date.");
  };

  const openEditModal = (homework) => {
    setIsEditMode(true);
    dispatch(setSelectedHomeworkMaster(homework));
    
    let parsedSessionIds = [];
    if (homework.session_master_ids && Array.isArray(homework.session_master_ids)) {
      parsedSessionIds = homework.session_master_ids.map(Number);
    } else if (homework.session_master_id) {
      parsedSessionIds = [Number(homework.session_master_id)];
    } else if (homework.assignments && Array.isArray(homework.assignments)) {
      parsedSessionIds = homework.assignments.filter(a => a.session_master_id).map(a => Number(a.session_master_id));
    }

    let parsedStudentIds = [];
    if (homework.student_ids && Array.isArray(homework.student_ids)) {
      parsedStudentIds = homework.student_ids.map(Number);
    } else if (homework.student_id) {
      parsedStudentIds = [Number(homework.student_id)];
    } else if (homework.recipients_roster && Array.isArray(homework.recipients_roster)) {
      parsedStudentIds = homework.recipients_roster.map(r => Number(r.student_id));
    } else if (homework.assignments && Array.isArray(homework.assignments)) {
      parsedStudentIds = homework.assignments.filter(a => a.student_id).map(a => Number(a.student_id));
    }

    let finalDueDate = homework.due_date || "";
    if (!finalDueDate && homework.assignments && homework.assignments[0]) {
      finalDueDate = homework.assignments[0].due_date || "";
    }

    setFormData({
      subject: homework.subject || "",
      topic: homework.topic || "",
      title: homework.title || "",
      description: homework.description || "",
      external_link: homework.external_link || "",
      assignment_type: homework.assignment_type || (parsedSessionIds.length > 0 ? "session" : "individual"),
      session_master_ids: parsedSessionIds,
      student_ids: parsedStudentIds,
      due_date: finalDueDate ? finalDueDate.replace(" ", "T").substring(0, 16) : "",
    });
    setStudentSearch("");
    setSessionSearch("");
    setActiveTab("details");
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.subject) {
      toastr.warning("Please select a subject scope context.");
      setActiveTab("details");
      return;
    }
    if (!formData.title) {
      toastr.warning("Homework title parameter is mandatory.");
      setActiveTab("details");
      return;
    }
    if (!formData.due_date) {
      toastr.warning("Valid target due date deadline is required.");
      setActiveTab("details");
      return;
    }
    if (formData.assignment_type === "session" && formData.session_master_ids.length === 0) {
      toastr.warning("Please configure target class session distribution scope.");
      setActiveTab("target");
      return;
    }
    if (formData.assignment_type === "individual" && formData.student_ids.length === 0) {
      toastr.warning("Please configure target individual student distribution scope.");
      setActiveTab("target");
      return;
    }

    setSaving(true);
    
    const submissionPayload = {
      subject: formData.subject,
      topic: formData.topic,
      title: formData.title,
      description: formData.description,
      external_link: formData.external_link,
      assignment_type: formData.assignment_type,
      due_date: formData.due_date,
      session_master_ids: formData.assignment_type === "session" ? formData.session_master_ids : [],
      student_ids: formData.assignment_type === "individual" ? formData.student_ids : []
    };

    try {
      if (isEditMode) {
        await dispatch(updateHomeworkMaster({ id: selectedHomeworkMaster.id, homeworkMasterData: submissionPayload })).unwrap();
        toastr.success("Homework synchronized successfully.");
      } else {
        await dispatch(addHomeworkMaster(submissionPayload)).unwrap();
        toastr.success("Homework assigned successfully.");
      }
      setShowModal(false);
      dispatch(fetchHomeworkMasters({ page: currentPage, limit, search: debouncedSearch }));
    } catch (err) {
      toastr.error(err || "Failed to save homework assignment.");
    } finally {
      setSaving(false);
    }
  };

  /* =====================================================
     NAMING & RESOLUTION CLEANERS
  ====================================================== */
  const getStudentName = (student) => student.student_name || student.name || "Unnamed Student";
  const getCategoryName = (category) => category.name || category.category_name || "Unnamed Category";

  const getStudentObject = (studentId) => {
    return globalStudents.find((s) => Number(s.id) === Number(studentId));
  };

  const getCleanSessionName = (masterId) => {
    const matched = masterSessionsList.find((m) => Number(m.id) === Number(masterId));
    if (!matched) return `Master Schedule #${masterId}`;

    const sessionName = matched.session_name || "";
    const is1to1 = matched.category === "1-1 Session" || matched.session_type === "1-1";
    const isOnline = matched.mode === "online" || String(sessionName).toLowerCase().includes("online");

    if (is1to1 || isOnline) {
      const firstStudentId = matched.students && matched.students[0];
      const studentObj = firstStudentId ? getStudentObject(firstStudentId) : null;
      const studentName = studentObj ? getStudentName(studentObj) : "";
      return studentName ? `${sessionName} - ${studentName}` : sessionName;
    } 
    
    let branchLabel = matched.branch_name || matched.branch || "";
    if (!branchLabel && matched.branch_id) {
      const matchedBranchObj = globalBranches.find((b) => Number(b.id) === Number(matched.branch_id));
      if (matchedBranchObj) {
        branchLabel = matchedBranchObj.branch_name || matchedBranchObj.name || "";
      }
    }
    
    return branchLabel ? `${sessionName} (${branchLabel})` : sessionName;
  };

  const getTargetScopeDisplay = (homework) => {
    let sessionIds = homework.session_master_ids || [];
    let studentIds = homework.student_ids || [];

    if (sessionIds.length === 0 && studentIds.length === 0 && homework.assignments) {
      homework.assignments.forEach(a => {
        if (a.session_master_id) sessionIds.push(a.session_master_id);
        if (a.student_id) studentIds.push(a.student_id);
      });
    }

    if (homework.assignment_type === "session" || sessionIds.length > 0) {
      const firstId = sessionIds[0] || homework.session_master_id;
      if (!firstId) return "No batches selected";
      if (sessionIds.length <= 1) return getCleanSessionName(firstId);
      return `${getCleanSessionName(firstId)} (+${sessionIds.length - 1} Batches)`;
    } else {
      const totalCount = studentIds.length || (homework.recipients_roster ? homework.recipients_roster.length : 0) || (homework.student_id ? 1 : 0);
      if (totalCount === 1) {
        const singleId = studentIds[0] || (homework.recipients_roster && homework.recipients_roster[0]?.student_id) || homework.student_id;
        const singleSt = getStudentObject(singleId);
        return singleSt ? getStudentName(singleSt) : "1 Student Profile";
      }
      return `${totalCount} Student Profiles Selected`;
    }
  };

  const formatDateDisplay = (d) => {
    let targetDate = d;
    if (!targetDate && selectedHomeworkMaster?.assignments && selectedHomeworkMaster.assignments[0]) {
      targetDate = selectedHomeworkMaster.assignments[0].due_date;
    }
    if (!targetDate) return "—";
    const dt = new Date(targetDate);
    return isNaN(dt.getTime()) ? targetDate : dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };

  const renderPagination = () =>
    Array.from({ length: totalPages }, (_, i) => i + 1).map((i) => (
      <button key={i} type="button" onClick={() => handlePageChange(i)} className={`hm-page-btn${currentPage === i ? " active" : ""}`}>
        {i}
      </button>
    ));

  const filteredStudents = globalStudents.filter((st) => getStudentName(st).toLowerCase().includes(studentSearch.toLowerCase()));
  const filteredSessions = masterSessionsList.filter((se) => getCleanSessionName(se.id).toLowerCase().includes(sessionSearch.toLowerCase()));

  return (
    <div className="hm-page">
      <div className="hm-wrap">

        {/* PAGE HEADER */}
        <div className="hm-header">
          <div>
            <p className="hm-eyebrow">Academy Operations · Admin Console</p>
            <h1 className="hm-title">Homework Registry</h1>
          </div>
          <button type="button" className="hm-btn-primary" onClick={openAddModal}>
            <i className="fas fa-plus"></i> Assign Homework
          </button>
        </div>

        {/* KPI METRICS STRIP */}
        <div className="hm-kpi-grid">
          {[
            { label: "Total Assigned Tasks", value: total },
            { label: "Showing Batches", value: homeworkMasters.length },
            { label: "Workspace Page", value: `${currentPage} / ${totalPages}` },
          ].map(({ label, value }) => (
            <div key={label} className="hm-kpi">
              <span className="hm-kpi-label">{label}</span>
              <span className="hm-kpi-val">{loading ? <Skeleton w={40} h={22} r={5} /> : value}</span>
            </div>
          ))}
        </div>

        {/* TOOLBAR */}
        <div className="hm-toolbar">
          <div className="hm-search">
            <i className="fas fa-search hm-search-icon"></i>
            <input type="text" placeholder="Search by title or topic..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
          </div>
        </div>

        {/* DATA TABLE CONTAINER */}
        <div className="hm-card">
          <div className="table-responsive">
            <table className="hm-table">
              <thead>
                <tr>
                  <th style={{ width: "28%" }}>Task / Reference</th>
                  <th style={{ width: "20%" }}>Subject &amp; Topic</th>
                  <th style={{ width: "22%" }}>Scope Allocation</th>
                  <th style={{ width: "13%" }}>Due Date</th>
                  <th className="text-end" style={{ width: "17%" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="hm-row">
                      <td><Skeleton w="75%" h={12} /><Skeleton w="40%" h={10} style={{ marginTop: 6 }} /></td>
                      <td><Skeleton w={60} h={22} r={20} /></td>
                      <td><Skeleton w={80} h={12} /></td>
                      <td><Skeleton w={90} h={12} /></td>
                      <td style={{ textAlign: "right" }}><Skeleton w={90} h={30} r={7} style={{ marginLeft: "auto" }} /></td>
                    </tr>
                  ))
                ) : homeworkMasters.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="hm-empty-state">
                      <div className="hm-empty-icon-wrap"><i className="fas fa-book-open"></i></div>
                      <p className="hm-empty-title">No homework profiles discovered</p>
                      <span className="hm-empty-desc">Create tasks for session groups or separate individual student profiles.</span>
                    </td>
                  </tr>
                ) : (
                  homeworkMasters.map((homework) => (
                    <tr key={homework.id} className="hm-row">
                      <td data-label="Task / Reference">
                        <div className="hm-name">{homework.title}</div>
                        {homework.external_link && (
                          <div className="hm-loc">
                            <a href={homework.external_link} target="_blank" rel="noreferrer" className="hm-link-url">
                              <i className="fas fa-link"></i> Resource Target
                            </a>
                          </div>
                        )}
                      </td>
                      <td data-label="Subject & Topic">
                        <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
                          <span className="hm-pill pill-blue">{homework.subject}</span>
                          {homework.topic && (
                            <span className="hm-topic-tag">
                              <i className="fas fa-tag" style={{ fontSize: 9 }}></i> {homework.topic}
                            </span>
                          )}
                        </div>
                      </td>
                      <td data-label="Scope Allocation">
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          <span className={`hm-pill-type ${homework.assignment_type === "session" ? "type-green" : "type-amber"}`}>
                            <i className={homework.assignment_type === "session" ? "fas fa-layer-group" : "fas fa-user-shield"}></i>
                            {homework.assignment_type === "session" ? " Batch Distribution" : " Isolated Targets"}
                          </span>
                          <span className="hm-roster-text">{getTargetScopeDisplay(homework)}</span>
                        </div>
                      </td>
                      <td data-label="Due Date">
                        <div className="hm-date-main">{formatDateDisplay(homework.due_date)}</div>
                      </td>
                      <td className="text-end" data-label="Actions">
                        <div className="d-inline-flex gap-1">
                          <button
                            type="button"
                            className="hm-btn-outline me-1"
                            title="Clone task details for another branch"
                            onClick={() => cloneHomework(homework)}
                          >
                            <i className="fas fa-copy me-1"></i> Clone
                          </button>
                          <button
                            type="button"
                            className="hm-btn-outline"
                            onClick={() => openEditModal(homework)}
                          >
                            Edit
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* PAGINATION PANEL */}
          <div className="hm-footer">
            <span className="hm-count">{homeworkMasters.length} of {total} records</span>
            <div className="hm-pagination">
              <button type="button" className="hm-page-btn hm-page-arrow" disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)}>
                <i className="fas fa-chevron-left"></i>
              </button>
              {renderPagination()}
              <button type="button" className="hm-page-btn hm-page-arrow" disabled={currentPage === totalPages} onClick={() => handlePageChange(currentPage + 1)}>
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>

      </div>

      {/* MODAL CONFIGURATION HUB */}
      {showModal && (
        <div className="hm-modal-overlay" onClick={(e) => e.target === e.currentTarget && !saving && setShowModal(false)}>
          <div className="hm-modal">

            {/* Sidebar Navigation */}
            <div className="hm-modal-side">
              <div className="hm-modal-side-header">
                <p className="hm-modal-side-title">Task Profile</p>
                <p className="hm-modal-side-sub">Configuration panel</p>
              </div>
              <nav className="hm-modal-nav">
                {[
                  { key: "details", icon: "fas fa-sliders-h", label: "Task details" },
                  { key: "target", icon: "fas fa-bullseye", label: "Distribution scope" },
                ].map(({ key, icon, label }) => (
                  <button key={key} type="button" className={`hm-nav-item${activeTab === key ? " active" : ""}`} onClick={() => setActiveTab(key)} disabled={saving}>
                    <i className={icon}></i>
                    <span>{label}</span>
                    {key === "target" && (
                      <span className="hm-nav-badge">
                        {formData.assignment_type === "session" ? formData.session_master_ids.length : formData.student_ids.length}
                      </span>
                    )}
                  </button>
                ))}
              </nav>
            </div>

            {/* Main Form Interface */}
            <form onSubmit={handleSubmit} className="hm-modal-main">
              <div className="hm-modal-header">
                <div>
                  <h2 className="hm-modal-title">{isEditMode ? "Edit task allocation" : "Assign task profile"}</h2>
                  <p className="hm-modal-sub">Configure objectives for multi-session or targeted individual scopes.</p>
                </div>
                <button type="button" className="btn-close shadow-none" onClick={() => setShowModal(false)} disabled={saving}></button>
              </div>

              <div className="hm-modal-body">
                <div className="row g-3">

                  {/* TAB: DETAILS */}
                  {activeTab === "details" && (
                    <>
                      <div className="col-md-6">
                        <label className="hm-label">Subject scope *</label>
                        <select name="subject" className="hm-field" value={formData.subject} onChange={handleChange}>
                          <option value="">Select subject</option>
                          {globalCategories.map((course) => (
                            <option key={course.id} value={getCategoryName(course)}>{getCategoryName(course)}</option>
                          ))}
                        </select>
                      </div>

                      <div className="col-md-6">
                        <label className="hm-label">Topic / Concept Identifier</label>
                        <input type="text" name="topic" className="hm-field" placeholder="e.g. sicilian_najdorf" value={formData.topic} onChange={handleChange} />
                      </div>

                      <div className="col-md-6">
                        <label className="hm-label">Submission deadline *</label>
                        <input type="datetime-local" name="due_date" className="hm-field" value={formData.due_date} onChange={handleChange} />
                      </div>

                      <div className="col-md-6">
                        <label className="hm-label">Resource / External URL link</label>
                        <input type="url" name="external_link" className="hm-field" placeholder="https://lichess.org/study/... or drive link" value={formData.external_link} onChange={handleChange} />
                      </div>

                      <div className="col-12">
                        <label className="hm-label">Homework title *</label>
                        <input type="text" name="title" className="hm-field" placeholder="e.g. Najdorf Main Line - Theory Review" value={formData.title} onChange={handleChange} />
                      </div>

                      <div className="col-12">
                        <label className="hm-label">Directives &amp; Context Instructions</label>
                        <textarea name="description" className="hm-field" rows="4" style={{ resize: "none" }} placeholder="Type core work details here..." value={formData.description} onChange={handleChange}></textarea>
                      </div>
                    </>
                  )}

                  {/* TAB: TARGET SCOPE */}
                  {activeTab === "target" && (
                    <>
                      <div className="col-12">
                        <label className="hm-label">Distribution Strategy Allocation</label>
                        <select name="assignment_type" className="hm-field" value={formData.assignment_type} onChange={handleChange}>
                          <option value="session">Distribute to Batches (Single or Multiple Groups)</option>
                          <option value="individual">Isolate Matrix to Targeted Students (Single or Multiple Roster)</option>
                        </select>
                      </div>

                      {/* Dynamic Session Code List View Tracker */}
                      {formData.assignment_type === "session" && (
                        <div className="col-12" style={{ marginTop: 15 }}>
                          <label className="hm-label">Configure Target Session Batches *</label>
                          <input type="text" className="hm-field" placeholder="Search batch or branch (e.g. Chidambaram)..." value={sessionSearch} onChange={(e) => setSessionSearch(e.target.value)} style={{ marginBottom: 10 }} />
                          <div className="hm-people-scroll">
                            <div className="hm-people-grid">
                              {filteredSessions.map((se) => {
                                const isChecked = formData.session_master_ids.includes(Number(se.id));
                                return (
                                  <label key={se.id} className={`hm-people-card${isChecked ? " selected" : ""}`} htmlFor={`session-check-${se.id}`}>
                                    <input type="checkbox" id={`session-check-${se.id}`} checked={isChecked} onChange={() => toggleSessionSelection(se.id)} />
                                    <div className="hm-people-avatar"><i className="fas fa-layer-group" style={{ fontSize: 11 }}></i></div>
                                    <div className="hm-people-copy">
                                      <div className="hm-people-name">{getCleanSessionName(se.id)}</div>
                                    </div>
                                    <div className="hm-people-check"><i className="fas fa-check"></i></div>
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Dynamic Single/Multiple Student Selection Matrix */}
                      {formData.assignment_type === "individual" && (
                        <div className="col-12" style={{ marginTop: 15 }}>
                          <label className="hm-label">Configure Target Student Profiles *</label>
                          <input type="text" className="hm-field" placeholder="Search roster profiles..." value={studentSearch} onChange={(e) => setStudentSearch(e.target.value)} style={{ marginBottom: 10 }} />
                          <div className="hm-people-scroll">
                            <div className="hm-people-grid">
                              {filteredStudents.map((st) => {
                                const isChecked = formData.student_ids.includes(Number(st.id));
                                const name = getStudentName(st);
                                return (
                                  <label key={st.id} className={`hm-people-card${isChecked ? " selected" : ""}`} htmlFor={`student-check-${st.id}`}>
                                    <input type="checkbox" id={`student-check-${st.id}`} checked={isChecked} onChange={() => toggleStudentSelection(st.id)} />
                                    <div className="hm-people-avatar">{(name[0] || "?").toUpperCase()}</div>
                                    <div className="hm-people-copy">
                                      <div className="hm-people-name">{name}</div>
                                      <div className="hm-people-branch">{st.admission_number || "Active Profile ID"}</div>
                                    </div>
                                    <div className="hm-people-check"><i className="fas fa-check"></i></div>
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                </div>
              </div>

              {/* MODAL ACTION CONTROL BUTTONS */}
              <div className="hm-modal-footer">
                <button type="button" className="hm-btn-cancel" onClick={() => setShowModal(false)} disabled={saving}>Cancel</button>
                <button type="submit" className="hm-btn-primary hm-btn-submit" disabled={saving}>
                  {saving ? (
                    <>
                      <Spinner size={15} color="#ffffff" thickness={2} />
                      {isEditMode ? "Saving modifications..." : "Processing allocations..."}
                    </>
                  ) : (
                    isEditMode ? "Save changes" : "Assign task"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── DESIGN SYSTEM STYLES ── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,400;0,14..32,500;0,14..32,600;1,14..32,400&display=swap');

        @keyframes hm-spin { to { transform: rotate(360deg); } }
        @keyframes hm-shimmer {
          0%   { background-position: -600px 0; }
          100% { background-position:  600px 0; }
        }
        @keyframes hm-modal-in {
          from { opacity: 0; transform: scale(0.97) translateY(10px); }
          to   { opacity: 1; transform: scale(1)    translateY(0);     }
        }
        @keyframes hm-overlay-in { from { opacity: 0; } to { opacity: 1; } }

        .hm-page {
          background: #f5f5f7;
          min-height: 100vh;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          color: #1d1d1f;
          font-size: 14px;
        }
        .hm-wrap { max-width: 1200px; margin: 0 auto; padding: 2rem 1.5rem; }

        .hm-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; flex-wrap: wrap; margin-bottom: 1.5rem; }
        .hm-eyebrow { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.07em; color: #aeaeb2; margin: 0 0 4px; }
        .hm-title { font-size: 1.35rem; font-weight: 600; color: #1d1d1f; letter-spacing: -0.022em; margin: 0; }

        .hm-kpi-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 1.25rem; }
        .hm-kpi { background: #fff; border: 1px solid #d2d2d7; border-radius: 12px; padding: 14px 18px; display: flex; flex-direction: column; gap: 5px; }
        .hm-kpi-label { font-size: 11px; color: #6e6e73; font-weight: 500; text-transform: uppercase; letter-spacing: 0.05em; }
        .hm-kpi-val { font-size: 1.5rem; font-weight: 600; color: #1d1d1f; letter-spacing: -0.025em; font-variant-numeric: tabular-nums; min-height: 1.8rem; display: flex; align-items: center; }

        .hm-toolbar { display: flex; align-items: center; gap: 10px; margin-bottom: 1rem; }
        .hm-search { flex: 1; position: relative; }
        .hm-search-icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #aeaeb2; font-size: 12px; }
        .hm-search input {
          width: 100%; background: #fff; border: 1px solid #d2d2d7; border-radius: 10px;
          padding: 9px 12px 9px 34px; font-size: 13px; color: #1d1d1f; outline: none;
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        .hm-search input:focus { border-color: #0071e3; box-shadow: 0 0 0 3px rgba(0,113,227,0.15); }

        .hm-btn-primary {
          background: #0071e3; color: #fff; border: none; border-radius: 10px; padding: 9px 18px;
          font-size: 13px; font-weight: 500; cursor: pointer; display: inline-flex; align-items: center; gap: 7px;
          transition: all 0.15s; white-space: nowrap; min-width: 140px; justify-content: center;
        }
        .hm-btn-primary:hover:not(:disabled) { background: #0077ed; }
        .hm-btn-primary:disabled { opacity: 0.65; cursor: not-allowed; }
        .hm-btn-submit { padding: 9px 22px; min-width: 158px; }

        .hm-card { background: #fff; border: 1px solid #d2d2d7; border-radius: 14px; overflow: hidden; }
        .hm-table { width: 100%; border-collapse: collapse; }
        .hm-table thead th {
          background: #f5f5f7; font-size: 10.5px; font-weight: 600; color: #6e6e73;
          text-transform: uppercase; letter-spacing: 0.07em; padding: 11px 18px; border-bottom: 1px solid #e8e8ed;
        }
        .hm-table tbody tr { border-bottom: 1px solid #f5f5f7; transition: background 0.1s; }
        .hm-row:hover { background: #fafafa; }
        .hm-table td { padding: 13px 18px; vertical-align: middle; text-align: left; }

        .hm-name { font-size: 13.5px; font-weight: 600; color: #1d1d1f; }
        .hm-loc { font-size: 11.5px; margin-top: 3px; }
        .hm-link-url { color: #0071e3; text-decoration: none; display: inline-flex; align-items: center; gap: 4px; font-weight: 500; }
        .hm-link-url:hover { text-decoration: underline; }

        .hm-pill { display: inline-block; padding: 3px 10px; border-radius: 20px; font-size: 11px; font-weight: 500; white-space: nowrap; }
        .pill-blue  { background: #dbeafe; color: #1e40af; border: 1px solid #bfdbfe; }
        
        .hm-topic-tag {
          font-size: 11px;
          color: #64748b;
          background: #f1f5f9;
          border: 1px solid #e2e8f0;
          padding: 2px 7px;
          border-radius: 6px;
          font-weight: 500;
          display: inline-flex;
          align-items: center;
          gap: 4px;
        }

        .hm-pill-type { display: inline-flex; align-items: center; gap: 4px; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.02em; }
        .type-green { color: #166534; }
        .type-amber { color: #d97706; }
        .hm-roster-text { font-size: 12.5px; color: #3c3c43; font-weight: 500; }

        .hm-date-main { font-size: 12.5px; font-weight: 500; color: #1d1d1f; font-variant-numeric: tabular-nums; }

        .hm-btn-outline {
          border: 1px solid #d2d2d7; background: #fff; border-radius: 8px; padding: 6px 12px;
          font-size: 12px; font-weight: 500; color: #3c3c43; cursor: pointer; transition: all 0.12s;
        }
        .hm-btn-outline:hover { border-color: #aeaeb2; background: #f5f5f7; color: #1d1d1f; }

        .hm-empty-state { text-align: center; padding: 3rem 1rem; }
        .hm-empty-icon-wrap { width: 52px; height: 52px; background: #f5f5f7; border-radius: 14px; display: inline-flex; align-items: center; justify-content: center; font-size: 1.4rem; color: #aeaeb2; margin-bottom: 12px; }
        .hm-empty-title { font-size: 14px; font-weight: 500; color: #3c3c43; margin: 0 0 4px; }
        .hm-empty-desc { font-size: 12px; color: #aeaeb2; }

        .hm-footer { display: flex; align-items: center; justify-content: space-between; padding: 12px 18px; border-top: 1px solid #f5f5f7; gap: 10px; flex-wrap: wrap; }
        .hm-count { font-size: 12px; color: #6e6e73; font-weight: 500; }
        .hm-pagination { display: flex; gap: 4px; }
        .hm-page-btn { min-width: 30px; height: 30px; padding: 0 6px; border: 1px solid #e8e8ed; background: #fff; border-radius: 7px; font-size: 12px; font-weight: 500; color: #3c3c43; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; transition: all 0.12s; }
        .hm-page-btn:hover:not(:disabled):not(.active) { background: #f5f5f7; border-color: #d2d2d7; }
        .hm-page-btn.active { background: #0071e3; color: #fff; border-color: #0071e3; }
        .hm-page-btn:disabled { opacity: 0.3; cursor: not-allowed; }

        .hm-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.36); backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; z-index: 9999; padding: 16px; animation: hm-overlay-in 0.2s ease; }
        .hm-modal { width: 100%; max-width: 900px; height: 590px; background: #fff; border-radius: 18px; border: 1px solid #d2d2d7; box-shadow: 0 32px 60px -12px rgba(0,0,0,0.18); display: flex; overflow: hidden; animation: hm-modal-in 0.24s cubic-bezier(0.34,1.1,0.64,1); }

        .hm-modal-side { width: 210px; background: #f5f5f7; border-right: 1px solid #e8e8ed; display: flex; flex-direction: column; flex-shrink: 0; }
        .hm-modal-side-header { padding: 18px 16px; border-bottom: 1px solid #e8e8ed; }
        .hm-modal-side-title { font-size: 13px; font-weight: 600; color: #1d1d1f; margin: 0; }
        .hm-modal-side-sub { font-size: 11px; color: #aeaeb2; margin: 3px 0 0; }
        
        .hm-modal-nav { padding: 8px; display: flex; flex-direction: column; gap: 2px; }
        .hm-nav-item { display: flex; align-items: center; gap: 9px; padding: 9px 12px; border-radius: 9px; border: none; background: transparent; font-size: 13px; color: #6e6e73; cursor: pointer; text-align: left; width: 100%; transition: all 0.12s; position: relative; }
        .hm-nav-item i { font-size: 12.5px; width: 14px; text-align: center; }
        .hm-nav-item span { flex: 1; }
        .hm-nav-item:hover:not(.active) { background: #ebebf0; color: #3c3c43; }
        .hm-nav-item.active { background: #fff; color: #0071e3; font-weight: 500; border: 1px solid #e8e8ed; }
        .hm-nav-badge { background: #0071e3; color: #fff; font-size: 10px; font-weight: 600; border-radius: 20px; padding: 1px 6px; flex-shrink: 0; }

        .hm-modal-main { flex: 1; display: flex; flex-direction: column; min-width: 0; }
        .hm-modal-header { padding: 18px 26px; border-bottom: 1px solid #f5f5f7; display: flex; align-items: flex-start; justify-content: space-between; }
        .hm-modal-title { font-size: 15px; font-weight: 600; color: #1d1d1f; margin: 0; }
        .hm-modal-sub { font-size: 12px; color: #6e6e73; margin: 3px 0 0; }
        .hm-modal-body { flex: 1; overflow-y: auto; padding: 22px 26px; }
        .hm-modal-footer { padding: 14px 26px; border-top: 1px solid #f5f5f7; display: flex; justify-content: flex-end; align-items: center; gap: 10px; background: #f5f5f7; }

        .hm-label { display: block; font-size: 11px; font-weight: 600; color: #6e6e73; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 5px; }
        .hm-field { width: 100%; border: 1px solid #d2d2d7; border-radius: 9px; padding: 9px 12px; font-size: 13px; color: #1d1d1f; background: #fff; outline: none; transition: all 0.15s; font-family: inherit; }
        .hm-field:focus { border-color: #0071e3; box-shadow: 0 0 0 3px rgba(0,113,227,0.15); }

        .hm-people-scroll { max-height: 250px; overflow-y: auto; padding-right: 4px; }
        .hm-people-scroll::-webkit-scrollbar { width: 4px; }
        .hm-people-scroll::-webkit-scrollbar-track { background: transparent; }
        .hm-people-scroll::-webkit-scrollbar-thumb { background: #d2d2d7; border-radius: 3px; }
        
        .hm-people-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px; }
        .hm-people-card {
          display: flex; align-items: center; gap: 10px; border: 1.5px solid #e8e8ed; border-radius: 10px; padding: 10px 12px;
          cursor: pointer; transition: all 0.12s; background: #fff; position: relative; user-select: none;
        }
        .hm-people-card:hover { border-color: #d2d2d7; background: #fafafa; }
        .hm-people-card.selected { border-color: #0071e3; background: #e8f0fe; }
        .hm-people-avatar {
          width: 34px; height: 34px; border-radius: 50%; background: #f5f5f7; color: #3c3c43; display: flex; align-items: center;
          justify-content: center; font-weight: 600; font-size: 13px; flex-shrink: 0; transition: all 0.12s;
        }
        .hm-people-card.selected .hm-people-avatar { background: #0071e3; color: #fff; }
        .hm-people-copy { min-width: 0; flex-grow: 1; }
        .hm-people-name { font-weight: 500; font-size: 12.5px; color: #1d1d1f; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .hm-people-branch { font-size: 11px; color: #8e8e93; margin-top: 1px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        
        .hm-people-check {
          width: 20px; height: 20px; border-radius: 4px; border: 1.5px solid #e8e8ed; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center; transition: all 0.12s; background: #fff;
        }
        .hm-people-card.selected .hm-people-check { background: #0071e3; border-color: #0071e3; color: #fff; }
        .hm-people-check i { font-size: 0.6rem; opacity: 0; transition: opacity 0.12s; }
        .hm-people-card.selected .hm-people-check i { opacity: 1; }
        .hm-people-card input { position: absolute; opacity: 0; pointer-events: none; }

        .hm-btn-cancel { border: 1px solid #d2d2d7; background: #fff; border-radius: 9px; padding: 9px 18px; font-size: 13px; font-weight: 500; color: #6e6e73; cursor: pointer; transition: all 0.12s; }
        .hm-btn-cancel:hover { background: #f5f5f7; color: #3c3c43; }

        @media (max-width: 991px) {
          .hm-kpi-grid { grid-template-columns: repeat(2, 1fr); }
        }

        @media (max-width: 768px) {
          .hm-wrap { padding: 1.25rem 1rem; }
          .hm-kpi-grid { grid-template-columns: 1fr; }
          .hm-table thead { display: none; }
          .hm-table tbody tr { display: block; border: 1px solid #e8e8ed; border-radius: 10px; margin-bottom: 12px; padding: 8px 14px; background: #fff; }
          .hm-table td { display: flex; justify-content: space-between; align-items: center; padding: 8px 0; border: none; width: 100% !important; text-align: left !important; }
          .hm-table td::before { content: attr(data-label); font-size: 11px; font-weight: 600; color: #8e8e93; text-transform: uppercase; letter-spacing: 0.03em; flex-shrink: 0; margin-right: 15px; display: inline-block; }
          .hm-table td.text-end, .hm-table td[data-label="Actions"] { justify-content: flex-end; }
          .hm-table td.text-end::before { display: none; }
          .hm-modal-overlay { padding: 0; }
          .hm-modal { flex-direction: column; height: 100vh; max-height: 100vh; width: 100vw; max-width: 100vw; border-radius: 0; border: none; animation: hm-overlay-in 0.2s ease; }
          .hm-modal-side { width: 100%; border-right: none; border-bottom: 1px solid #e8e8ed; }
          .hm-modal-side-header { display: none; }
          .hm-modal-nav { flex-direction: row; overflow-x: auto; padding: 8px; gap: 6px; scroll-behavior: smooth; -webkit-overflow-scrolling: touch; }
          .hm-modal-nav::-webkit-scrollbar { display: none; }
          .hm-nav-item { white-space: nowrap; width: auto; font-size: 12.5px; padding: 8px 14px; border: 1px solid transparent; }
          .hm-nav-item.active { border-color: #d2d2d7; background: #fff; }
          .hm-modal-body { padding: 16px; }
          .hm-modal-header { padding: 16px 20px; }
          .hm-modal-footer { padding: 12px 20px; background: #fff; border-top: 1px solid #e8e8ed; }
          .hm-people-grid { grid-template-columns: 1fr; }
          .hm-people-scroll { max-height: calc(100vh - 280px); }
        }

        @media (max-width: 480px) {
          .hm-header { flex-direction: column; align-items: stretch; gap: 12px; }
          .hm-btn-primary { width: 100%; justify-content: center; }
          .hm-modal-footer { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
          .hm-btn-cancel, .hm-btn-submit { width: 100%; min-width: 0; margin: 0; text-align: center; }
          .hm-footer { flex-direction: column; align-items: stretch; text-align: center; }
          .hm-pagination { justify-content: center; }
        }
      `}</style>
    </div>
  );
};

export default HomeworkList;